/**
 * `/licenses/:licenseId` — one license: the key, the devices, the state.
 *
 * This is the screen the whole portal exists for. Two jobs, and they are the
 * two most common support emails a per-machine license generates:
 *
 *   "What is my key?"          → the key panel
 *   "I got a new PC and it     → the device list, with a release button on
 *    says I'm out of seats"      every row
 *
 * ── The key is not fetched until it is asked for ────────────────────────────
 *
 * aloftwx-api audits every open, so a panel that revealed on mount would write
 * a credential-read row for every idle page load, and would put a live key into
 * every screenshot a customer attaches to a support email. The prefix is shown
 * by default because it is enough for most conversations — "does yours start
 * with ALOFTK7M?" confirms they are holding the right key without reading it —
 * and it is an indexed, non-secret column anyway.
 *
 * The console pins this behaviour with a test, for a reason worth repeating
 * here: the failure looks like a *nicer* screen.
 *
 * ── There is no rotate button, and there must not be one ────────────────────
 *
 * Rotation means "this key leaked, kill it". A customer who reaches for it
 * expecting "show me my key" breaks their own reinstall with no way back, and
 * for a Polar license it hands them a key Polar's portal will contradict. It
 * stays an operator action behind a typed reason. See the list of deliberate
 * omissions in `shared/customer_contract.ts`.
 */

import { PageHeader } from "@/components/PageHeader";
import { StatusChip } from "@/components/StatusChip";
import {
  Empty,
  Field,
  FieldGrid,
  LeaderRow,
  Notice,
  Strip,
  StripBody,
  StripHead,
  Tag,
  TearLine,
} from "@/components/deck";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PortalError, portalApi, useApiMutation, useApiQuery } from "@/lib/api";
import type { CustomerLicenseDetail, CustomerSeat } from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import { isoDate, relative, stamp } from "@/lib/format";
import { useToast } from "@/lib/toast";
import { ArrowLeft, Check, Copy, Eye, Laptop } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { PRODUCT_LINKS, editionName } from "../../shared/portal";

export function LicenseDetailRoute() {
  const { licenseId = "" } = useParams<{ licenseId: string }>();

  const license = useApiQuery([licenseId], (signal) => portalApi.getLicense(licenseId, signal), {
    suppressToast: true,
  });

  if (license.isLoading) return <DetailSkeleton />;

  if (license.error) {
    return <DetailError error={license.error} onRetry={license.refetch} />;
  }

  if (!license.data) return null;
  return <LicenseDetail license={license.data} onChanged={license.refetch} />;
}

function LicenseDetail({
  license,
  onChanged,
}: {
  license: CustomerLicenseDetail;
  onChanged: () => void;
}) {
  const links = PRODUCT_LINKS[license.product];
  const atLimit = license.seats_used >= license.seat_limit;

  return (
    <>
      <PageHeader
        code="LIC"
        label="License"
        title={license.product_name}
        adornment={<StatusChip status={license.status} />}
        description={
          <>
            {editionName(license.edition)} edition, bought {isoDate(license.created_at)}.
          </>
        }
        actions={
          <Button asChild variant="ghost" size="sm">
            <Link to="/">
              <ArrowLeft aria-hidden="true" />
              All licenses
            </Link>
          </Button>
        }
      />

      {/* The state a customer must read before anything else on the page. A
          revoked or suspended license explains itself here in the operator's
          own words — `status_reason` is written to be shown to a customer. */}
      {license.status !== "active" ? <StatusNotice license={license} /> : null}

      <div className="flex flex-col gap-6">
        <KeyPanel license={license} />

        <SeatsPanel license={license} atLimit={atLimit} onChanged={onChanged} />

        <section aria-labelledby="details-head">
          <Strip bar={false}>
            <StripHead title="Details" as="h2" id="details-head" />
            <FieldGrid className="sm:grid-cols-2 lg:grid-cols-4">
              <Field label="Edition" value={editionName(license.edition)} mono={false} />
              {/* The tone is spread rather than passed as `atLimit ? "marker"
                  : undefined`: under `exactOptionalPropertyTypes` an optional
                  prop cannot be handed an explicit undefined, and the spread is
                  the form that says "this prop is absent" rather than "this
                  prop is set to nothing". */}
              <Field
                label="Devices"
                value={`${license.seats_used} / ${license.seat_limit}`}
                note={atLimit ? "At the limit" : "In use / allowed"}
                {...(atLimit ? { tone: "marker" as const } : {})}
              />
              <Field label="Purchased" value={isoDate(license.created_at)} />
              <Field
                label="Bought through"
                value={SOURCE_LABEL[license.source] ?? license.source}
                mono={false}
              />
            </FieldGrid>

            <StripBody className="flex flex-col gap-2.5">
              {license.entitlements.length > 0 ? (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="t-field">Includes</span>
                  {license.entitlements.map((entitlement) => (
                    <Tag key={entitlement} dot={false}>
                      {entitlement}
                    </Tag>
                  ))}
                </div>
              ) : null}
              {license.polar_order_id ? (
                <LeaderRow label="Order" value={license.polar_order_id} />
              ) : null}
              {links?.download ? (
                <div className="pt-1">
                  <Button asChild variant="outline" size="sm">
                    <a href={links.download}>Download the Downwind Manager</a>
                  </Button>
                </div>
              ) : null}
            </StripBody>
          </Strip>
        </section>
      </div>
    </>
  );
}

const SOURCE_LABEL: Readonly<Record<string, string>> = {
  polar: "downwind.studio",
  comp: "A complimentary copy",
  beta: "The beta programme",
  reseller: "A reseller",
};

/**
 * Suspended and revoked read very differently and must not share a panel.
 *
 * Suspended is reversible and usually a billing question, so it is the pen mark
 * — somebody needs to look at this. Revoked is terminal, so it is the hold
 * tone, and the copy does not offer false hope about fixing it from here.
 */
function StatusNotice({ license }: { license: CustomerLicenseDetail }) {
  const revoked = license.status === "revoked";
  return (
    <Notice
      tone={revoked ? "hold" : "marker"}
      label={revoked ? "This license has been revoked" : "This license is suspended"}
      className="mb-6"
      action={
        <Button asChild variant="outline" size="sm">
          <Link to="/support">Get help</Link>
        </Button>
      }
    >
      {license.status_reason ? (
        <>
          {license.status_reason}
          {revoked
            ? " Your key will not activate any new machines."
            : " Existing machines keep working; new activations are blocked until this is lifted."}
        </>
      ) : revoked ? (
        "Your key will not activate any new machines. If this is unexpected, get in touch and we will look at it."
      ) : (
        "Existing machines keep working; new activations are blocked until this is lifted."
      )}
    </Notice>
  );
}

/**
 * The key: prefix by default, full value on request.
 *
 * The reveal is one-way within a page view. Offering a "hide" button would
 * suggest the key had been un-fetched, which is not true — it is in the page,
 * it is in the audit log, and pretending otherwise is worse than not offering
 * it. Navigating away is what un-reveals it, and that is honest.
 */
function KeyPanel({ license }: { license: CustomerLicenseDetail }) {
  const { pushToast } = useToast();
  const [key, setKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const reveal = useApiMutation(() => portalApi.revealKey(license.id), {
    suppressToast: true,
    onSuccess: (result) => setKey(result.key),
  });

  async function copy() {
    if (!key) return;
    // Reported rather than assumed: the failure mode is a key that LOOKS copied
    // and is not, and the customer then pastes their previous clipboard entry
    // into the activation box and blames the key.
    if (await copyText(key)) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } else {
      pushToast({
        kind: "error",
        title: "Could not copy to the clipboard.",
        body: "Select the key and copy it by hand.",
      });
    }
  }

  const notStored = reveal.error instanceof PortalError && reveal.error.status === 409;

  return (
    <section aria-labelledby="key-head">
      <Strip>
        <StripHead
          title="License key"
          as="h2"
          id="key-head"
          note="Paste this into the app the first time you run it on a machine."
        >
          {key ? (
            <Button variant="outline" size="sm" onClick={copy}>
              {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
              {copied ? "Copied" : "Copy"}
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => reveal.mutate(undefined)}
              disabled={reveal.isPending || notStored}
            >
              <Eye aria-hidden="true" />
              {reveal.isPending ? "Fetching…" : "Show key"}
            </Button>
          )}
        </StripHead>

        <StripBody className="pl-5">
          <output
            className="t-data block break-all text-[1.0625rem] leading-relaxed text-ink"
            aria-live="polite"
          >
            {key ?? (
              <>
                {license.key_prefix}
                <span className="text-ink-3" aria-label="rest of the key hidden">
                  {" "}
                  ···· ···· ····
                </span>
              </>
            )}
          </output>

          {!key && !reveal.error ? (
            <p className="mt-3 text-[0.8125rem] leading-snug text-ink-2">
              We show the first {license.key_prefix.length} characters by default — enough to check
              you are holding the right key without putting the whole thing on your screen.
            </p>
          ) : null}

          {reveal.error ? (
            <Notice
              tone={notStored ? "marker" : "hold"}
              label={notStored ? "Cannot be shown" : "Could not fetch the key"}
              className="mt-4"
              action={
                notStored ? (
                  <Button asChild variant="outline" size="sm">
                    <Link to="/support">Get help</Link>
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => reveal.mutate(undefined)}>
                    Try again
                  </Button>
                )
              }
            >
              {reveal.error.message}
            </Notice>
          ) : null}
        </StripBody>
      </Strip>
    </section>
  );
}

/**
 * The devices, and the button that resolves the most common ticket this product
 * generates.
 *
 * No confirmation dialog on release, deliberately. The action is
 * self-correcting — a machine the customer is still using re-activates on its
 * next heartbeat — so a modal would be friction on a safe action, on the screen
 * somebody reaches while already frustrated. The console gates its bulk release
 * behind a typed reason because that one is not self-correcting in the same
 * way, and because an operator is acting on somebody else's license.
 */
function SeatsPanel({
  license,
  atLimit,
  onChanged,
}: {
  license: CustomerLicenseDetail;
  atLimit: boolean;
  onChanged: () => void;
}) {
  const { pushToast } = useToast();
  const [releasing, setReleasing] = useState<string | null>(null);

  const release = useApiMutation(
    (activationId: string) => portalApi.releaseSeat(license.id, activationId),
    {
      onSuccess: (result) => {
        // `released: 0` is a legitimate answer — the seat was already gone,
        // usually because of a double tap on a slow connection. Saying
        // "released" would be a lie; saying nothing would look broken.
        pushToast({
          kind: "success",
          title:
            result.released > 0
              ? "Device released. That slot is free now."
              : "That device had already been released.",
        });
        onChanged();
      },
      onError: () => onChanged(),
    },
  );

  async function releaseSeat(seat: CustomerSeat) {
    setReleasing(seat.activation_id);
    try {
      await release.mutateAsync(seat.activation_id);
    } catch {
      /* surfaced by the mutation's toast */
    } finally {
      setReleasing(null);
    }
  }

  return (
    <section aria-labelledby="seats-head">
      <Strip tone={atLimit ? "marker" : "neutral"} bar={atLimit}>
        <StripHead
          title="Devices"
          as="h2"
          id="seats-head"
          note={
            atLimit
              ? "Every slot is in use. Release one you no longer use and the next machine can activate straight away."
              : "Each machine you activate on takes one slot. Release one whenever you like — there is no waiting period."
          }
        >
          <Tag tone={atLimit ? "marker" : "neutral"} dot={false}>
            {license.seats_used} / {license.seat_limit}
          </Tag>
        </StripHead>

        {license.seats.length === 0 ? (
          <Empty>
            Nothing has activated on this key yet. Install the app, paste the key above, and the
            machine will appear here.
          </Empty>
        ) : (
          <div>
            {license.seats.map((seat) => (
              <SeatRow
                key={seat.activation_id}
                seat={seat}
                busy={releasing === seat.activation_id}
                disabled={release.isPending}
                onRelease={() => releaseSeat(seat)}
              />
            ))}
          </div>
        )}
      </Strip>

      {license.seats.length > 1 ? (
        <>
          <TearLine className="mt-6" />
          <p className="mt-4 text-[0.8125rem] leading-relaxed text-ink-2">
            Not sure which is which? Go by <span className="text-ink">last seen</span> — the machine
            you are reading this on will be the most recent. Releasing the wrong one costs nothing:
            it re-appears the next time that machine checks in.
          </p>
        </>
      ) : null}
    </section>
  );
}

/**
 * `label` and `os` are client-reported and absent on older builds, so a row has
 * to be identifiable by its dates alone. That is why `last_seen_at` is a
 * first-class column here and not a tooltip: it is the field a customer
 * actually recognises a machine by.
 */
function SeatRow({
  seat,
  busy,
  disabled,
  onRelease,
}: {
  seat: CustomerSeat;
  busy: boolean;
  disabled: boolean;
  onRelease: () => void;
}) {
  const name = seat.label ?? "Unnamed device";

  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-3 border-b border-rule px-4 py-4 pl-5 last:border-b-0">
      <Laptop aria-hidden="true" className="size-4 shrink-0 text-ink-3" />

      <div className="min-w-0 flex-1 basis-48">
        <div className="truncate text-[0.9375rem] text-ink" title={name}>
          {name}
        </div>
        <div className="t-field mt-1 truncate">{seat.os ?? "Operating system not reported"}</div>
      </div>

      <div className="min-w-0">
        <div className="t-field">Last seen</div>
        <div className="t-data mt-1 text-[0.875rem] text-ink" title={stamp(seat.last_seen_at)}>
          {relative(seat.last_seen_at)}
        </div>
      </div>

      <div className="min-w-0 max-sm:hidden">
        <div className="t-field">Activated</div>
        <div className="t-data mt-1 text-[0.875rem] text-ink-2">{isoDate(seat.activated_at)}</div>
      </div>

      <Button
        variant="outline"
        size="sm"
        onClick={onRelease}
        disabled={disabled}
        className="ml-auto shrink-0"
      >
        {busy ? "Releasing…" : "Release"}
      </Button>
    </div>
  );
}

function DetailSkeleton() {
  return (
    <>
      <div className="mb-7">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="mt-4 h-8 w-64" />
      </div>
      <div className="flex flex-col gap-6">
        <Strip>
          <StripHead title="License key" as="h2" />
          <StripBody className="pl-5">
            <Skeleton className="h-5 w-3/4" />
          </StripBody>
        </Strip>
        <Strip>
          <StripHead title="Devices" as="h2" />
          <StripBody className="flex flex-col gap-3 pl-5">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </StripBody>
        </Strip>
      </div>
    </>
  );
}

/**
 * 404 here means "not on your account", and it is deliberately the same answer
 * as "no such license" — the API cannot tell the caller which, so this screen
 * must not appear to know either. See rule 2 in `shared/customer_contract.ts`.
 */
function DetailError({ error, onRetry }: { error: Error; onRetry: () => void }) {
  const missing = error instanceof PortalError && error.status === 404;
  const unreachable = error instanceof PortalError && error.status === 503;

  return (
    <>
      <PageHeader
        code="LIC"
        label="License"
        title={missing ? "We cannot find that license" : "Could not load this license"}
        actions={
          <Button asChild variant="ghost" size="sm">
            <Link to="/">
              <ArrowLeft aria-hidden="true" />
              All licenses
            </Link>
          </Button>
        }
      />
      <Notice
        tone={missing ? "marker" : "hold"}
        label={missing ? "Not on this account" : "Load failed"}
        action={
          missing ? (
            <Button asChild variant="outline" size="sm">
              <Link to="/claim">Add a key</Link>
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={onRetry}>
              Try again
            </Button>
          )
        }
      >
        {missing ? (
          <>
            This license is not registered to the address you are signed in with. If you bought it
            under another email, add its key here and it will move onto this account.
          </>
        ) : unreachable ? (
          <>
            Your license is fine — this is our side. The app on your PC keeps working while this is
            down.
          </>
        ) : (
          error.message
        )}
      </Notice>
    </>
  );
}
